import { Tab, Tabs } from '@mui/material';
import { Col, Row } from '@design-system/components/grid/grid';
import {
  ChallengeDetailsVM,
  SubmissionsTab,
} from '../view-model/ChallengeDetails.viewModel';
import { _noop, keys } from '@client/new/helpers/general';
import { Typography } from '@design-system/components/typography/typography';
import { SubmissionModal } from './Submission.view';
import { FavIcon } from './components/FavButton';
import { emptyDiscountsCol } from '../../promotions/view/Discounts.view.css';
import { SelectableButton } from './components/SelectableButton';
import { TMap, toOption } from '@client/new/helpers/types';
import { Breadcrumbs } from './components/common/Breadcrumbs';
import { BackHeader } from './components/common/BackHeader';
import { DropDownFilter } from '@design-system/components/dropDown/dropDownFilter';
import { SearchInput } from '@design-system/components/searchinput';
import { userStatuses } from '../model/Challenges.types';
import * as styles from './ChallengeDetails.view.css';
import { SubmissionsGrid } from './components/SubmissionsGrid';
import { IconButton } from '@design-system/components/button/iconButton';
import { faArrowDownToLine } from '@fortawesome/pro-light-svg-icons';

export const ChallengeView = (p: ChallengeDetailsVM) => (
  <Col>
    <Breadcrumbs breadcrumbs={p.breadcrumbs} />
    <BackHeader
      backURL={p.backURL}
      title={`Submissions`}
      pillTitle={p.programName}
      pillColor="P-20"
    >
      {p.submissions.length > 0 ? (
        <IconButton
          size="medium"
          color="neutral"
          icon={faArrowDownToLine}
          onClick={() => p.dispatch('ExportCSV')}
        >
          Export CSV
        </IconButton>
      ) : null}
    </BackHeader>
    <ChallengeViewContent {...p} />
  </Col>
);

const tabs: TMap<SubmissionsTab, React.ReactNode> = {
  all: 'All',
  shortlisted: (
    <Row>
      <FavIcon full />
      <Typography style={{ marginTop: -2 }}>Shortlist</Typography>
    </Row>
  ),
};

const ChallengeViewContent = (p: ChallengeDetailsVM) => {
  if (p.totalSubmissions === 0) return <EmptySubmissions />;
  return (
    <>
      <Tabs value={p.activeTab} onChange={(_, t) => p.dispatch('SetTab', t)}>
        {keys(tabs).map((hash) => (
          <Tab
            key={hash}
            value={hash}
            label={tabs[hash]}
            iconPosition="start"
            className={styles.tabWrapper}
          />
        ))}
      </Tabs>
      <SubmissionModal {...p} />

      <Col className={styles.contentWrapper}>
        <Filters {...p} />
        <SubmissionsGrid {...p} />
      </Col>
    </>
  );
};

const Filters = (p: ChallengeDetailsVM) => (
  <Col gap="l">
    <Row>
      <SearchInput
        inputWrapperClassName={styles.searchWrapper}
        placeholder="Search by name or email"
        value={p.searchText}
        onChange={(e) => p.dispatch('SetSearchText', e.target.value)}
        onClear={() => p.dispatch('SetSearchText', '')}
      />
    </Row>
    <Row>
      {p.filters.map((f) =>
        f.value === 'UserStatus' ? (
          <DropDownFilter
            key={f.value}
            label="User Status"
            options={userStatuses.map((v) => toOption(v, v))}
            onSelect={(v) => p.dispatch('SetUserStatusFilter', v)}
            value={f.selected}
          />
        ) : (
          <SelectableButton
            onClick={() => p.dispatch('ToggleFilter', f.value)}
            isSelected={f.checked}
            key={f.value}
          >
            {f.label}
          </SelectableButton>
        ),
      )}
    </Row>
    <Row>
      <Typography fontWeight="normal" size="small" variant="body" color="N-60">
        Total submissions: {`${p.submissions.length}`}
      </Typography>
    </Row>
  </Col>
);

const EmptySubmissions = () => (
  <Col className={emptyDiscountsCol}>
    <Col style={{ maxWidth: 500 }}>
      <Typography variant="headline" size="medium">
        No submissions yet
      </Typography>
      <Typography variant="bodyLarge" fontWeight="medium" color="B-60">
        Challenge submissions will appear here once participants share their
        progress. Encourage your community to join the challenge and submit
        their work!
      </Typography>
    </Col>
  </Col>
);
